import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { groupLatestReactionsByType, isCurrentUserReaction } from '../communityChatReactions';

const removeButtonSx = {
  ml: 'auto',
  height: 32,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 1.5,
  color: '#b45309',
  borderColor: '#fdba74',
  '&:hover': {
    borderColor: 'primary.dark',
    bgcolor: 'action.hover',
  },
};

const CommunityReactionDetailSheet = ({
  open,
  message,
  currentUserIds,
  readOnly = false,
  removing = false,
  onClose,
  onRemoveReaction,
}) => {
  const groups = groupLatestReactionsByType(message?.latestReactions ?? []);
  const canRemoveReactions = !readOnly && Boolean(onRemoveReaction);

  const handleRemove = (type) => {
    if (!message?.id || removing) return;
    onRemoveReaction?.(message, type);
  };

  return (
    <Dialog open={open} onClose={removing ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          fontWeight: 700,
          fontSize: '1rem',
        }}
      >
        Reactions
        <IconButton aria-label="Close reactions" onClick={onClose} disabled={removing} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 2 }}>
        {groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No reactions yet.
          </Typography>
        ) : (
          groups.map((group) => (
            <Box key={group.type} sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, mb: 1 }}
              >
                {group.emoji} {group.reactions.length}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {group.reactions.map((reaction, index) => {
                  const isOwnReaction =
                    canRemoveReactions && isCurrentUserReaction(reaction.user, currentUserIds);

                  return (
                    <Box
                      key={`${group.type}-${reaction.user?.id ?? index}`}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}
                    >
                      <Avatar
                        src={reaction.user?.avatarUrl}
                        alt={reaction.user?.fullName}
                        sx={{ width: 32, height: 32 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        {reaction.user?.fullName ?? 'Member'}
                      </Typography>
                      {isOwnReaction ? (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={removing}
                          onClick={() => handleRemove(group.type)}
                          sx={removeButtonSx}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CommunityReactionDetailSheet;
